import { z } from 'zod'

/**
 * 결제 API 계약 (BIZ-70, 2축의 결제 축). 스파이크 결정: BIZ-69.
 *
 * 선불 흐름: 주문 생성(서버 금액 재계산·결제 대기) → 위젯 결제 → confirm(완료) → 30분 유예.
 * 돈이 걸린 영역이라 금액은 서버가 스냅샷(order_item)에서 재계산해 확정한다 — 응답의 amount는
 * 클라가 보낸 값이 아니라 서버 확정액이다. confirm 실제 연동(토스 호출·웹훅)은 다음 슬라이스.
 *
 * ⚠️ 공급가·수익 등 내부 금액은 이 응답에 없다. payment.amount = 기업이 낼 판매 확정액뿐.
 */

export const paymentStatus = z.enum([
  'READY', 'IN_PROGRESS', 'DONE', 'WAITING_DEPOSIT',
  'CANCELED', 'PARTIAL_CANCELED', 'FAILED',
])
export type PaymentStatus = z.infer<typeof paymentStatus>

/** 결제 레코드 뷰. 결제키·내부 원본(raw)·MID는 노출하지 않는다. */
export const paymentView = z.object({
  id: z.number().int(),
  orderId: z.number().int(),
  status: paymentStatus,
  amount: z.number().int(), // 서버 재계산 확정액
  /** 토스로 넘길 주문키(merchant orderId). FE 위젯이 결제 요청에 쓴다. */
  orderKey: z.string(),
  method: z.string().nullable(),
  approvedAt: z.string().nullable(),
  createdAt: z.string(),
  /** 누적 취소액(BIZ-96). 전액취소면 amount와 같다. 취소 없으면 0. */
  canceledAmount: z.number().int(),
})
export type PaymentView = z.infer<typeof paymentView>

export const createPaymentResponse = z.object({ payment: paymentView })
export type CreatePaymentResponse = z.infer<typeof createPaymentResponse>

/** confirm 요청 — 토스 successUrl에서 받은 3값. amount는 서버 저장액과 대조(위변조 차단). */
export const confirmPaymentRequest = z.object({
  paymentKey: z.string().min(1, 'paymentKey가 필요합니다'),
  orderKey: z.string().min(1, 'orderKey가 필요합니다'),
  amount: z.number().int().nonnegative(),
})
export type ConfirmPaymentRequest = z.infer<typeof confirmPaymentRequest>

/**
 * 취소/환불 요청 (BIZ-96). **운영자 수동 트리거만**(PRD §3.2 — 자동환불 아님).
 * cancelAmount 생략 시 남은 잔액 전액을 취소한다(전액취소). 지정 시 잔액 이하여야 한다(부분취소).
 */
export const cancelPaymentRequest = z.object({
  cancelAmount: z.number().int().positive().optional(),
  reason: z.string().min(1, '취소 사유가 필요합니다'),
})
export type CancelPaymentRequest = z.infer<typeof cancelPaymentRequest>

export const cancelPaymentResponse = z.object({ payment: paymentView })
export type CancelPaymentResponse = z.infer<typeof cancelPaymentResponse>

export const PaymentErrorCode = {
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  ORDER_NOT_FOUND: 'ORDER_NOT_FOUND',
  FORBIDDEN: 'FORBIDDEN',
  ORDER_NOT_PAYABLE: 'ORDER_NOT_PAYABLE', // 결제할 스냅샷(order_item)이 없거나 확정액이 0
  PAYMENT_NOT_FOUND: 'PAYMENT_NOT_FOUND',
  PAYMENT_NOT_CONFIRMABLE: 'PAYMENT_NOT_CONFIRMABLE', // READY/IN_PROGRESS가 아님
  AMOUNT_MISMATCH: 'AMOUNT_MISMATCH', // 저장액 ≠ 클라 amount ≠ 토스 totalAmount
  PAYMENT_PROVIDER_NOT_CONFIGURED: 'PAYMENT_PROVIDER_NOT_CONFIGURED', // 토스 연동 미구현(스텁)
  PAYMENT_CONFIRM_REJECTED: 'PAYMENT_CONFIRM_REJECTED', // 토스가 승인 거절(카드사 오류 등)
  PAYMENT_NOT_CANCELABLE: 'PAYMENT_NOT_CANCELABLE', // DONE/PARTIAL_CANCELED가 아니거나 결제키 없음
  INVALID_CANCEL_AMOUNT: 'INVALID_CANCEL_AMOUNT', // 0 이하이거나 남은 잔액 초과
  PAYMENT_CANCEL_REJECTED: 'PAYMENT_CANCEL_REJECTED', // 토스가 취소 거절
} as const
export type PaymentErrorCode = (typeof PaymentErrorCode)[keyof typeof PaymentErrorCode]
